import { v4 as uuidV4 } from "uuid";
import { Identifiable, IdentityProvider, Planzone } from "@planzone/dto";
import { HttpClient, HttpMethods } from "@planzone/services";

const prefix = "cas.authn.pac4j.";

function extractIndex(key: string): number {
  const res = key.match(/\d+/);

  return res && res.length > 0 ? parseInt(res[0]) || 0 : 0;
}

function extractProp(key: string): {
  name: string;
  providerName: string;
  index: number;
} {
  const elements = key.split(".");
  const name = elements[elements.length - 1];

  return {
    providerName: elements.length === 2 ? "cas" : elements[1] || "",
    name,
    index: elements.length > 0 ? extractIndex(elements[0]) : 0,
  };
}

function getIdentityProviders(attributes: {
  [key: string]: string;
}): (Identifiable<IdentityProvider> & { index: number })[] {
  return Object.values(
    Object.entries(attributes)
      .filter(([key]) => key.slice(0, prefix.length) === prefix)
      .reduce<{
        [key: string]: Identifiable<IdentityProvider> & { index: number };
      }>((providers, [key, value]) => {
        const [_, ...rest] = key.split(prefix);
        const prop = extractProp(rest.join());
        const currentProvider = providers[prop.index] ?? {};
        const baseProvider = {
          ...currentProvider,
          name: prop.providerName,
          index: prop.index,
        };

        return {
          ...providers,
          [prop.index]:
            prop.name === "uuid"
              ? {
                  ...baseProvider,
                  uuid: value,
                }
              : prop.name === "enable" || prop.name === "enabled"
                ? {
                    ...baseProvider,
                    isEnabled: value === "true",
                  }
                : {
                    ...baseProvider,
                    config: {
                      ...(providers[prop.index]
                        ? providers[prop.index].config
                        : {}),
                      [prop.name]: value,
                    },
                  },
        };
      }, {}),
  );
}

function formatIdentityProvider(
  identityProvider: IdentityProvider,
  index: number,
  id: string,
): { [key: string]: string } {
  const isCas = identityProvider.name === "cas";
  const fullPrefix = `${prefix}${isCas ? `cas[${index}]` : `oidc[${index}]`}${!isCas ? `.${identityProvider.name}` : ""}`;

  return Object.entries(identityProvider.config).reduce<{
    [key: string]: string;
  }>(
    (acc, [key, value]) => ({
      ...acc,
      [`${fullPrefix}.${key}`]: value,
    }),
    {
      [`${fullPrefix}.${isCas ? "enable" : "enabled"}`]:
        identityProvider.isEnabled ? "true" : "false",
      [`${fullPrefix}.uuid`]: id,
    },
  );
}

function getTypeIndex(
  identityProvider: IdentityProvider,
  identityProviders: (Identifiable<IdentityProvider> & { index: number })[],
): number {
  return (
    identityProviders
      .filter(({ name }) =>
        identityProvider.name === "cas" ? name === "cas" : name !== "cas",
      )
      .reduce<number>(
        (highestIndex, provider) =>
          provider.index > highestIndex ? provider.index : highestIndex,
        0,
      ) + 1
  );
}

function saveIdentityProvider(httpClient: HttpClient) {
  return (
    { uuid }: Identifiable<Planzone>,
    identityProvider: IdentityProvider,
  ) =>
    httpClient
      .sendHttpRequest<Identifiable<Planzone>>({
        endpoint: `/planzones/${uuid}`,
        method: HttpMethods.GET,
      })
      .then(({ attributes }) => {
        const providerId = uuidV4();
        const identityProviders = getIdentityProviders(attributes);

        return httpClient
          .sendHttpRequest<void>({
            endpoint: `/planzones/${uuid}`,
            method: HttpMethods.PATCH,
            data: {
              attributes: {
                ...formatIdentityProvider(
                  identityProvider,
                  getTypeIndex(identityProvider, identityProviders),
                  providerId,
                ),
              },
            },
          })
          .then(() => ({ ...identityProvider, uuid: providerId }));
      });
}

export { saveIdentityProvider };
